// tslint:disable-next-line:nx-enforce-module-boundaries
import { inject } from '@angular/core';
import { FooterComponent } from '@epicuro-next/content';
import { ActiveAccountGuard } from '@epicuro-next/features/accounts-selector';
import { SpotlightComponent } from '@epicuro-next/features/spotlight';
import {
  APP_PRIVATE_PATHS,
  APP_TOP_LEVEL_PATH,
  EpcRoutes,
} from '@epicuro-next/navigation/constants';
import { MainNavigationComponent } from '@epicuro-next/navigation/main-navigation';
import { AuthGuard } from '@epicuro-next/platform/authentication';
import { selectEnableOrderBookV2 } from '@epicuro-next/platform/configuration';
import { UserTermsGuard } from '@epicuro-next/state/user-state';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';

import { clearUrlParamsGuardFactory } from '../services/clear-url-params-guard';
import { offboardingDeactivateGuard } from '../services/offboarding.guard';
import { UrlResolverGuard } from '../services/url-resolver.guard';

/**
 * Routes protected by the Authorization Guard
 * Put here the child routes that need to be behind the Auth Guard.
 *
 * 'footer' and 'main-navigation' are optional router outlets.
 */
export const privateRoutes: EpcRoutes = [
  // Currently following object will result in error
  // https://github.com/angular/angular/pull/38379
  // {
  //   path: '',
  //   outlet: 'spotlight',
  //   loadChildren: () =>
  //     import('@epicuro-next/features/spotlight').then((m) => m.FeaturesSpotlightModule),
  //   canLoad: [CanLoadFeatureService],
  // },
  // TODO: Uncomment above and delete below line. Remove as soon as Angular 11.
  // Move spotlight feature flag from FeaturesSpotlightModule to `canLoad: [CanLoadFeatureService],`
  // will be rolled out - https://github.com/angular/angular/pull/38379#issuecomment-695328656

  {
    path: APP_PRIVATE_PATHS.spotlight.path,
    component: SpotlightComponent,
    outlet: APP_PRIVATE_PATHS.spotlight.outlet,
  },

  {
    path: APP_PRIVATE_PATHS.footer.path,
    component: FooterComponent,
    outlet: APP_PRIVATE_PATHS.footer.outlet,
  },

  {
    path: APP_PRIVATE_PATHS.mainNavigation.path,
    component: MainNavigationComponent,
    outlet: APP_PRIVATE_PATHS.mainNavigation.outlet,
    children: [
      {
        path: APP_PRIVATE_PATHS.informationBanner.path,
        loadChildren: () =>
          import('@epicuro-next/features/information-banner').then(
            (m) => m.InformationBannerModule,
          ),
      },
    ],
  },

  {
    path: '',
    pathMatch: 'full',
    redirectTo: APP_PRIVATE_PATHS.home.path, // will be listened by routes and decide where
    // it should be redirected: newsFeed, landingPage or landingPageStory
  },

  {
    path: APP_PRIVATE_PATHS.home.path,
    loadChildren: () =>
      import('@epicuro-next/features/home').then((m) => m.HomeModule),
    data: APP_PRIVATE_PATHS.home.data,
  },
  {
    path: APP_PRIVATE_PATHS.story.path,
    loadChildren: () =>
      import('@epicuro-next/features/story').then((m) => m.FeaturesStoryModule),
    data: APP_PRIVATE_PATHS.story.data,
  },
  {
    path: APP_PRIVATE_PATHS.browse.path,
    loadChildren: () =>
      import('@epicuro-next/navigation/browse').then(
        (m) => m.NavigationBrowseModule,
      ),
    data: APP_PRIVATE_PATHS.browse.data,
  },
  {
    path: APP_PRIVATE_PATHS.search.path,
    redirectTo: `${APP_PRIVATE_PATHS.browse.path}/${APP_PRIVATE_PATHS.search.path}`,
  },
  {
    path: APP_PRIVATE_PATHS.shopTheLook.path,
    redirectTo: `${APP_PRIVATE_PATHS.browse.path}/${APP_PRIVATE_PATHS.shopTheLook.path}`,
  },
  {
    path: APP_PRIVATE_PATHS.productList.path,
    redirectTo: `${APP_PRIVATE_PATHS.browse.path}/${APP_PRIVATE_PATHS.productList.path}`,
  },
  {
    path: APP_PRIVATE_PATHS.lookBook.path,
    loadChildren: () =>
      import('@epicuro-next/features/look-book').then((m) => m.LookBookModule),
    data: APP_PRIVATE_PATHS.lookBook.data,
  },
  {
    path: APP_PRIVATE_PATHS.basket.path,
    loadChildren: () =>
      import('@epicuro-next/features/cbasket').then((m) => m.CbasketModule),
    data: APP_PRIVATE_PATHS.basket.data,
  },
  {
    path: APP_PRIVATE_PATHS.scan.path,
    loadChildren: () =>
      import('@epicuro-next/features/scan').then((m) => m.FeaturesScanModule),
    data: APP_PRIVATE_PATHS.scan.data,
  },
  {
    canMatch: [
      () =>
        inject(Store)
          .select(selectEnableOrderBookV2)
          .pipe(map((value) => !value)),
    ],
    canDeactivate: [clearUrlParamsGuardFactory()],
    path: APP_PRIVATE_PATHS.orderBook.path,
    loadChildren: () =>
      import('@epicuro-next/features/order-book').then(
        (m) => m.OrderBookModule,
      ),
    data: APP_PRIVATE_PATHS.orderBook.data,
  },
  {
    canMatch: [
      () => inject(Store).select(selectEnableOrderBookV2).pipe(map(Boolean)),
    ],
    canDeactivate: [clearUrlParamsGuardFactory()],
    path: APP_PRIVATE_PATHS.orderBook.path,
    loadChildren: () =>
      import('@epicuro-next/features/order-book-v2').then(
        (m) => m.FeaturesOrderBookV2Module,
      ),
    data: {
      ...APP_PRIVATE_PATHS.orderBook.data,
      showSpotlight: false,
    },
  },
  {
    path: APP_PRIVATE_PATHS.cmi.path,
    canDeactivate: [clearUrlParamsGuardFactory()],
    loadChildren: () =>
      import('@epicuro-next/features/cmi').then((m) => m.CmiModule),
    data: APP_PRIVATE_PATHS.cmi.data,
  },
  {
    path: APP_PRIVATE_PATHS.supportPage.path,
    loadChildren: () =>
      import('@epicuro-next/features/support-page').then(
        (m) => m.SupportPageModule,
      ),
    data: APP_PRIVATE_PATHS.supportPage.data,
  },
  {
    path: APP_PRIVATE_PATHS.basketManager.path,
    loadChildren: () =>
      import('@epicuro-next/features/full-basket-manager').then(
        (m) => m.BasketManagerModule,
      ),
    data: APP_PRIVATE_PATHS.orderBook.data,
  },
  {
    path: APP_PRIVATE_PATHS.builder.path,
    loadChildren: () =>
      import('@epicuro-next/addons/builder-content').then(
        (m) => m.BuilderContentModule,
      ),
    data: APP_PRIVATE_PATHS.builder.data,
  },
  // Below three is a precaution for legacy routing logic
  {
    path: APP_PRIVATE_PATHS.homeNewsFeed.path,
    redirectTo: APP_PRIVATE_PATHS.home.path,
  },
  {
    path: APP_PRIVATE_PATHS.homeLandingPage.path,
    redirectTo: APP_PRIVATE_PATHS.home.path,
  },
  {
    path: APP_PRIVATE_PATHS.homeLandingPageStory.path,
    redirectTo: APP_PRIVATE_PATHS.home.path,
  },
  {
    path: `${APP_PRIVATE_PATHS.home.path}/:homeId`,
    loadChildren: () =>
      import('@epicuro-next/features/landing').then(
        (m) => m.FeaturesLandingModule,
      ),
  },
  {
    path: APP_PRIVATE_PATHS.dashboard.path,
    loadChildren: () =>
      import('@epicuro-next/features/dashboard').then((m) => m.DashboardModule),
    data: APP_PRIVATE_PATHS.dashboard.data,
  },
  {
    path: `${APP_PRIVATE_PATHS.sellerTools.path}/${APP_PRIVATE_PATHS.basketReminder.path}`,
    canDeactivate: [clearUrlParamsGuardFactory()],
    loadComponent: () =>
      import('@epicuro-next/features/seller-tools/basket-reminder').then(
        (m) => m.BasketReminderPageComponent,
      ),
  },
  {
    path: `${APP_PRIVATE_PATHS.sellerTools.path}/${APP_PRIVATE_PATHS.budget.path}`,
    canDeactivate: [clearUrlParamsGuardFactory()],
    loadChildren: () =>
      import('@epicuro-next/features/seller-tools/seller-tools-budget').then(
        (m) => m.FeaturesSellerToolsBudgetModule,
      ),
  },
  {
    path: APP_PRIVATE_PATHS.sellerTools.path,
    redirectTo: `${APP_PRIVATE_PATHS.sellerTools.path}/${APP_PRIVATE_PATHS.budget.path}`,
  },
  {
    path: `${APP_PRIVATE_PATHS.invoices.path}`,
    canDeactivate: [
      clearUrlParamsGuardFactory([`${APP_PRIVATE_PATHS.orderBook.path}/`]),
    ],
    loadComponent: () =>
      import('@epicuro-next/features/invoices').then(
        (m) => m.InvoicesPageComponent,
      ),
  },
  {
    path: APP_PRIVATE_PATHS.liveStream.path,
    loadComponent: () =>
      import('@epicuro-next/features/live-stream').then(
        (m) => m.LiveStreamComponent,
      ),
    children: [
      {
        path: ':versionId',
        loadComponent: () =>
          import('@epicuro-next/features/live-stream').then(
            (m) => m.LiveStreamComponent,
          ),
      },
    ],
  },
  {
    path: `${APP_PRIVATE_PATHS.mediaRelations.path}`,
    loadComponent: () =>
      import('@epicuro-next/features/media-relations').then(
        (m) => m.MediaRelationsPageComponent,
      ),
  },
  {
    path: APP_PRIVATE_PATHS.pockets.path,
    loadChildren: () =>
      import('@epicuro-next/features/pockets').then(
        (m) => m.FeaturesPocketsModule,
      ),
    data: {
      showSpotlight: false,
    },
  },
];

export const allOtherRoutes = [
  {
    path: '**',
    redirectTo: '',
  },
];

/**
 * Main routes.
 */
export const routes: EpcRoutes = [
  {
    path: APP_TOP_LEVEL_PATH.accountSelector.path,
    canLoad: [AuthGuard],
    loadChildren: () =>
      import('@epicuro-next/features/accounts-selector').then(
        (m) => m.AccountsSelectorRoutingModule,
      ),
    data: APP_TOP_LEVEL_PATH.accountSelector.data,
  },
  {
    path: APP_TOP_LEVEL_PATH.userTerms.path,
    canLoad: [AuthGuard],
    canActivate: [ActiveAccountGuard],
    loadChildren: () =>
      import('@epicuro-next/features/terms-and-conditions/route').then(
        (m) => m.UserTermsRouteModule,
      ),
    data: APP_TOP_LEVEL_PATH.userTerms.data,
  },
  {
    path: APP_TOP_LEVEL_PATH.authentication.path,
    loadChildren: () =>
      import('@epicuro-next/features/authentication').then(
        (m) => m.AuthenticationUIModule,
      ),
  },
  {
    path: APP_PRIVATE_PATHS.tradeTerms.path,
    canLoad: [AuthGuard],
    canActivate: [ActiveAccountGuard, UserTermsGuard],
    loadChildren: () =>
      import('@epicuro-next/features/terms-and-conditions/route').then(
        (m) => m.TradeTermsRouteModule,
      ),
    data: APP_PRIVATE_PATHS.tradeTerms.data,
  },
  {
    path: APP_TOP_LEVEL_PATH.cs.path,
    canLoad: [AuthGuard],
    outlet: APP_TOP_LEVEL_PATH.cs.outlet,
    loadChildren: () =>
      import('@epicuro-next/features/config-strip/route').then(
        (m) => m.ConfigStripRouteModule,
      ),
  },
  {
    path: APP_TOP_LEVEL_PATH.siteSelector.path,
    canLoad: [AuthGuard],
    canActivate: [UrlResolverGuard, ActiveAccountGuard, UserTermsGuard],
    loadChildren: () =>
      import('@epicuro-next/features/site-selector').then(
        (m) => m.FeaturesSiteSelectorModule,
      ),
  },
  {
    path: APP_PRIVATE_PATHS.privacyStatement.path,
    canLoad: [AuthGuard],
    canActivate: [ActiveAccountGuard],
    loadChildren: () =>
      import('@epicuro-next/features/privacy-statement').then(
        (m) => m.PrivacyStatementModule,
      ),
    data: APP_PRIVATE_PATHS.privacyStatement.data,
  },
  {
    path: APP_PRIVATE_PATHS.userAgreement.path,
    canLoad: [AuthGuard],
    canActivate: [ActiveAccountGuard, UserTermsGuard],
    loadChildren: () =>
      import('@epicuro-next/features/user-agreement').then(
        (m) => m.UserAgreementModule,
      ),
    data: APP_PRIVATE_PATHS.userAgreement.data,
  },
  {
    path: APP_TOP_LEVEL_PATH.urlResolver.path,
    canLoad: [AuthGuard],
    canActivate: [ActiveAccountGuard],
    loadChildren: () =>
      import('@epicuro-next/features/url-resolver').then(
        (m) => m.UrlResolverModule,
      ),
  },
  {
    path: APP_TOP_LEVEL_PATH.offboarding.path,
    canLoad: [AuthGuard],
    canActivate: [ActiveAccountGuard],
    canDeactivate: [offboardingDeactivateGuard],
    loadComponent: () =>
      import('@epicuro-next/features/offboarding').then(
        (m) => m.OffboardingComponent,
      ),
  },
];
